import { devLog } from './devLog';
import { sanitizeAIResponse } from './chatUtils';

/**
 * Extract raw AI response text from API data based on model format.
 */
export const extractAIResponse = (data, selectedModel) => {
  let aiRawResponse = "";

  if (selectedModel === "deepseek-r1:32b") {
    aiRawResponse = data.response || "";
  } else if (selectedModel === "kaidex-local") {
    devLog("🔍 DEBUG: Extracting kaidex-local response");
    devLog("🔍 DEBUG: data.response exists?", !!data.response);
    devLog("🔍 DEBUG: data.response.choices exists?", !!data.response?.choices);
    devLog("🔍 DEBUG: data.response.choices[0] exists?", !!data.response?.choices?.[0]);
    devLog("🔍 DEBUG: data.response.choices[0].message exists?", !!data.response?.choices?.[0]?.message);
    devLog("🔍 DEBUG: data.response.choices[0].message.content:", data.response?.choices?.[0]?.message?.content);
    aiRawResponse = data.response?.choices?.[0]?.message?.content || data.choices?.[0]?.message?.content || "";
  } else if (selectedModel.startsWith("gpt-") || selectedModel.startsWith("o4-")) {
    aiRawResponse = data.response?.choices?.[0]?.message?.content || data.choices?.[0]?.message?.content || "";
  } else if (selectedModel.startsWith("grok-")) {
    aiRawResponse = data.choices?.[0]?.message?.content || "";
  } else if (selectedModel.includes("qwen") || selectedModel.includes("claude") || selectedModel.includes("gemini")) {
    aiRawResponse = data.response?.choices?.[0]?.message?.content || data.choices?.[0]?.message?.content || "";
  } else {
    aiRawResponse = data.choices?.[0]?.message?.content || data.response?.choices?.[0]?.message?.content || "";
  }

  devLog(`💬 Extracted AI response:`, aiRawResponse);
  return aiRawResponse;
};

/**
 * Check if response contains a tool call pattern that backend should handle.
 */
export const isToolCallResponse = (aiRawResponse) => {
  const toolCallPattern = /\[web_search\(query="[^"]+"\)\]/;
  return toolCallPattern.test(aiRawResponse);
};

/**
 * Extract all rich media and tool results from API response data.
 */
export const extractRichMedia = (data) => {
  const mediaResults = data.media || null;
  if (mediaResults) {
    devLog(`🎬 Rich media received: ${mediaResults.images?.length || 0} images, ${mediaResults.videos?.length || 0} videos`);
  }

  const sourcesResults = data.sources || null;
  if (sourcesResults) {
    devLog(`📚 Sources received: ${sourcesResults.length} cited documents`);
  }

  const diagramResults = data.diagrams || null;
  if (diagramResults) {
    devLog(`📊 Diagrams received: ${diagramResults.length} diagram(s)`);
  }

  const spreadsheetResults = data.spreadsheets || null;
  if (spreadsheetResults) {
    devLog(`📊 Spreadsheets received: ${spreadsheetResults.length} sheet(s)`);
  }

  const widgetResults = data.widgets || null;
  if (widgetResults) {
    devLog(`🎨 Widgets received: ${widgetResults.length} widget(s)`);
  }

  const circuitResults = data.circuits || null;
  if (circuitResults) {
    devLog(`🔌 Circuits received: ${circuitResults.length} circuit(s)`);
  }

  const userInputRequest = data.user_input_request || null;
  if (userInputRequest) {
    devLog(`❓ QnA received: ${userInputRequest.length} question(s)`);
  }

  return { mediaResults, sourcesResults, diagramResults, spreadsheetResults, widgetResults, circuitResults, userInputRequest };
};

/**
 * Build the final AI message object from parsed response.
 */
export const buildAIMessage = (aiRawResponse, selectedModel, richMedia) => {
  const aiResponse = sanitizeAIResponse(aiRawResponse);
  const { mediaResults, sourcesResults, diagramResults, spreadsheetResults, widgetResults, circuitResults } = richMedia;

  const text = selectedModel === "deepseek-r1:32b"
    ? aiResponse
        .replace(/<think>[\s\S]*?<\/think>/g, "")
        .replace(/DeepSeek-R1/g, "Chatty")
        .replace(/DeepSeek/g, "KAI")
        .replace(/深度求索/g, "LKC")
        .trim()
        .replace(/[^\S\r\n]+/g, " ")
    : aiResponse.trim();

  return {
    role: "ai",
    text,
    ...(mediaResults && { media: mediaResults }),
    ...(sourcesResults && { sources: sourcesResults }),
    ...(diagramResults && { diagrams: diagramResults }),
    ...(spreadsheetResults && { spreadsheets: spreadsheetResults }),
    ...(widgetResults && { widgets: widgetResults }),
    ...(circuitResults && { circuits: circuitResults })
  };
};
